import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, disputeService, jobService } from '../services/api';
import { DisputeStatus, DisputeCategory, DisputeParty, DisputeResolutionType } from '../types/modules';
import type { DisputeDto, DisputeSummaryDto } from '../types/modules';
import type { JobSummaryDto } from '../types';

const STATUS_LABELS: Record<DisputeStatus, string> = {
  [DisputeStatus.Open]: 'Open',
  [DisputeStatus.InReview]: 'In Review',
  [DisputeStatus.Proposed]: 'Resolution Proposed',
  [DisputeStatus.Resolved]: 'Resolved',
  [DisputeStatus.Escalated]: 'Escalated',
  [DisputeStatus.Closed]: 'Closed',
  [DisputeStatus.Withdrawn]: 'Withdrawn',
};
const STATUS_COLOURS: Record<DisputeStatus, { bg: string; text: string }> = {
  [DisputeStatus.Open]: { bg: '#fef3c7', text: '#92400e' },
  [DisputeStatus.InReview]: { bg: '#dbeafe', text: '#1e40af' },
  [DisputeStatus.Proposed]: { bg: '#ede9fe', text: '#6d28d9' },
  [DisputeStatus.Resolved]: { bg: '#dcfce7', text: '#15803d' },
  [DisputeStatus.Escalated]: { bg: '#fee2e2', text: '#991b1b' },
  [DisputeStatus.Closed]: { bg: '#f3f4f6', text: '#374151' },
  [DisputeStatus.Withdrawn]: { bg: '#f3f4f6', text: '#374151' },
};
const CATEGORY_LABELS: Record<DisputeCategory, string> = {
  [DisputeCategory.ItemDamaged]: 'Item Damaged',
  [DisputeCategory.ItemMissing]: 'Item Missing',
  [DisputeCategory.LateDelivery]: 'Late Delivery',
  [DisputeCategory.CarrierNoShow]: 'Carrier No-Show',
  [DisputeCategory.IncorrectDelivery]: 'Incorrect Delivery',
  [DisputeCategory.PaymentDisputed]: 'Payment Disputed',
  [DisputeCategory.FraudSuspected]: 'Fraud Suspected',
  [DisputeCategory.VehicleRepairQuality]: 'Repair Quality',
  [DisputeCategory.RelayHandoverFailed]: 'Relay Handover Failed',
  [DisputeCategory.Other]: 'Other',
};
const RESOLUTION_LABELS: Record<DisputeResolutionType, string> = {
  [DisputeResolutionType.FullRefundToCustomer]: 'Full Refund to Customer',
  [DisputeResolutionType.PartialRefundToCustomer]: 'Partial Refund',
  [DisputeResolutionType.NoRefund]: 'No Refund',
  [DisputeResolutionType.CarrierPenalty]: 'Carrier Penalty Applied',
  [DisputeResolutionType.MutualAgreement]: 'Mutual Agreement',
  [DisputeResolutionType.EscalatedToLegal]: 'Escalated to Legal',
};

const REPLYABLE = new Set([DisputeStatus.Open, DisputeStatus.InReview, DisputeStatus.Proposed]);

function toBase64(file: File): Promise<{ base64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      resolve({ base64: dataUrl.split(',')[1], mimeType: file.type || 'application/octet-stream' });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Open dispute modal ────────────────────────────────────────────────────
function OpenDisputeModal({ myJobs, onOpened, onClose, client }: {
  myJobs: JobSummaryDto[];
  onOpened: (d: DisputeDto) => void;
  onClose: () => void;
  client: ReturnType<typeof createApiClient>;
}) {
  const [jobRef, setJobRef] = useState('');
  const [category, setCategory] = useState<DisputeCategory>(DisputeCategory.Other);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const matchedJob = myJobs.find(j => j.referenceNumber.toLowerCase() === jobRef.trim().toLowerCase());

  const submit = async () => {
    if (!matchedJob) { setError('Pick a job from the list, or type its exact reference number.'); return; }
    if (!title.trim() || !description.trim()) { setError('Please fill in all required fields.'); return; }
    setSubmitting(true); setError('');
    try {
      const evidenceUrls = files.length > 0
        ? await disputeService(client).uploadEvidence(await Promise.all(files.map(toBase64)))
        : undefined;
      const dispute = await disputeService(client).open({
        jobId: matchedJob.id,
        raisedByParty: DisputeParty.Customer,
        category, title: title.trim(), description: description.trim(),
        evidenceUrls,
      });
      onOpened(dispute);
    } catch (e: any) {
      setError(e.message ?? 'Failed to open dispute.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 480, boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}>
        <h2 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: '0 0 16px' }}>Open a Dispute</h2>

        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '8px 12px', borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>{error}</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>JOB *</label>
            <input
              list="my-disputes-jobs-list"
              value={jobRef}
              onChange={e => setJobRef(e.target.value)}
              placeholder="Pick a job, or type its reference number"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box' }}
            />
            <datalist id="my-disputes-jobs-list">
              {myJobs.map(j => (
                <option key={j.id} value={j.referenceNumber}>{j.title}</option>
              ))}
            </datalist>
            {jobRef.trim() && !matchedJob && (
              <p style={{ fontSize: 11.5, color: '#dc2626', margin: '4px 0 0' }}>No job of yours matches that reference.</p>
            )}
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>CATEGORY *</label>
            <select value={category} onChange={e => setCategory(Number(e.target.value))}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' }}>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>TITLE *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Brief summary of the issue"
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box' }} />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>DESCRIPTION *</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4}
              placeholder="Describe the issue in detail."
              style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>PHOTOS / VIDEOS (optional)</label>
            <input type="file" accept="image/*,video/*" multiple
              onChange={e => setFiles(Array.from(e.target.files ?? []))}
              style={{ fontSize: 12.5 }} />
            {files.length > 0 && <p style={{ fontSize: 11.5, color: '#6b7280', margin: '4px 0 0' }}>{files.length} file{files.length > 1 ? 's' : ''} selected</p>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button onClick={submit} disabled={submitting} style={{
            flex: 1, padding: 10, background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8,
            fontWeight: 700, fontSize: 13.5, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.6 : 1,
          }}>{submitting ? 'Opening…' : 'Open Dispute'}</button>
          <button onClick={onClose} style={{ flex: 1, padding: 10, background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, color: '#374151', fontSize: 13.5, cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Dispute detail / thread ───────────────────────────────────────────────
function DisputeDetail({ dispute, onUpdated, client }: {
  dispute: DisputeDto;
  onUpdated: (d: DisputeDto) => void;
  client: ReturnType<typeof createApiClient>;
}) {
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [responding, setResponding] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [dispute.updates]);

  const send = async () => {
    if (!message.trim() && files.length === 0) return;
    setSending(true);
    try {
      const attachmentUrls = files.length > 0
        ? await disputeService(client).uploadEvidence(await Promise.all(files.map(toBase64)))
        : undefined;
      const updated = await disputeService(client).addUpdate(dispute.id, {
        authorParty: DisputeParty.Customer, message: message.trim() || '(attachment)', attachmentUrls,
      });
      onUpdated(updated);
      setMessage(''); setFiles([]);
    } catch { /* surfaced via the unchanged thread state */ } finally { setSending(false); }
  };

  const respond = async (accepted: boolean) => {
    setResponding(true);
    try {
      await disputeService(client).respondToResolution(dispute.id, accepted);
      onUpdated({ ...dispute, status: accepted ? DisputeStatus.Resolved : DisputeStatus.InReview });
    } finally { setResponding(false); }
  };

  const colours = STATUS_COLOURS[dispute.status];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <p style={{ fontWeight: 700, fontSize: 14.5, color: '#111827', margin: 0 }}>{dispute.title}</p>
            <p style={{ fontSize: 12, color: '#6b7280', margin: '3px 0 0' }}>
              Ref {dispute.referenceNumber} · {CATEGORY_LABELS[dispute.category]}
              {dispute.slaBreached && <span style={{ color: '#dc2626', fontWeight: 600 }}> · SLA breached</span>}
            </p>
          </div>
          <span style={{ fontSize: 11.5, fontWeight: 700, padding: '3px 9px', borderRadius: 6, background: colours.bg, color: colours.text, whiteSpace: 'nowrap' }}>
            {STATUS_LABELS[dispute.status]}
          </span>
        </div>

        {dispute.status === DisputeStatus.Resolved && dispute.resolutionType && (
          <div style={{ marginTop: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>
            <strong style={{ color: '#15803d' }}>Resolved: </strong>{RESOLUTION_LABELS[dispute.resolutionType]}
            {!!dispute.refundAmount && <span style={{ color: '#15803d', marginLeft: 8 }}>£{dispute.refundAmount.toFixed(2)} refund</span>}
          </div>
        )}

        {dispute.status === DisputeStatus.Proposed && dispute.resolutionType && (
          <div style={{ marginTop: 10, background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '10px 12px' }}>
            <p style={{ fontSize: 13, color: '#5b21b6', fontWeight: 600, margin: '0 0 2px' }}>Proposed resolution</p>
            <p style={{ fontSize: 13, color: '#6d28d9', margin: 0 }}>
              {RESOLUTION_LABELS[dispute.resolutionType]}
              {!!dispute.refundAmount && <span> — £{dispute.refundAmount.toFixed(2)}</span>}
            </p>
            {dispute.resolutionNotes && <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>{dispute.resolutionNotes}</p>}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={() => respond(true)} disabled={responding} style={{ padding: '6px 14px', background: '#15803d', color: '#fff', border: 'none', borderRadius: 7, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                Accept Resolution
              </button>
              <button onClick={() => respond(false)} disabled={responding} style={{ padding: '6px 14px', background: '#fff', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 7, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
                Reject Resolution
              </button>
            </div>
          </div>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {dispute.updates.map(u => (
          <div key={u.id} style={{ background: '#f9fafb', border: '1px solid #f3f4f6', borderRadius: 10, padding: '8px 12px' }}>
            <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 3px' }}>
              {new Date(u.createdAt).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}
            </p>
            <p style={{ fontSize: 13, color: '#111827', margin: 0, whiteSpace: 'pre-wrap' }}>{u.message}</p>
            {u.attachmentUrls && u.attachmentUrls.length > 0 && (
              <p style={{ fontSize: 11.5, color: '#1d4ed8', margin: '4px 0 0' }}>{u.attachmentUrls.length} attachment{u.attachmentUrls.length > 1 ? 's' : ''}</p>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {REPLYABLE.has(dispute.status) && (
        <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <textarea value={message} onChange={e => setMessage(e.target.value)} rows={2}
            placeholder="Add a message or evidence to your dispute"
            style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, resize: 'none', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="file" accept="image/*,video/*" multiple onChange={e => setFiles(Array.from(e.target.files ?? []))} style={{ fontSize: 12, flex: 1 }} />
            <button onClick={send} disabled={sending || (!message.trim() && files.length === 0)} style={{
              padding: '7px 16px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8,
              fontWeight: 700, fontSize: 12.5, cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.6 : 1,
            }}>Send</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function MyDisputesPage() {
  const { getToken, account } = useAppAuth();
  const [disputes, setDisputes] = useState<DisputeDto[]>([]);
  const [myJobs, setMyJobs] = useState<JobSummaryDto[]>([]);
  const [summary, setSummary] = useState<DisputeSummaryDto | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!account) { setLoading(false); return; }
    setLoading(true);
    const client = createApiClient(getToken);
    try {
      const [d, jobs, s] = await Promise.all([
        disputeService(client).getMine(),
        jobService(client).getMyJobs(),
        disputeService(client).getSummary().catch(() => null),
      ]);
      setDisputes(d);
      setMyJobs(jobs);
      if (s) setSummary(s);
      if (d.length > 0 && !selectedId) setSelectedId(d[0].id);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, getToken]);

  useEffect(() => { load(); }, [load]);

  const selected = disputes.find(d => d.id === selectedId) ?? null;
  const handleUpdated = (updated: DisputeDto) => setDisputes(prev => prev.map(d => (d.id === updated.id ? updated : d)));
  const handleOpened = (d: DisputeDto) => { setDisputes(prev => [d, ...prev]); setSelectedId(d.id); setShowOpenModal(false); };

  return (
    <div style={{ width: '100%', padding: '24px 16px' }}>
      {showOpenModal && (
        <OpenDisputeModal
          myJobs={myJobs}
          onOpened={handleOpened}
          onClose={() => setShowOpenModal(false)}
          client={createApiClient(getToken)}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <Link to="/my-dashboard" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>← Dashboard</Link>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '8px 0 4px' }}>My Disputes</h1>
          {summary && (
            <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
              {summary.openCount} open
              {summary.overdueSlaCount > 0 && <span style={{ color: '#dc2626' }}> · {summary.overdueSlaCount} SLA breached</span>}
              {summary.avgResolutionHours > 0 && <span> · avg {summary.avgResolutionHours.toFixed(1)}h resolution</span>}
            </p>
          )}
        </div>
        <button onClick={() => setShowOpenModal(true)} style={{
          background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13.5, padding: '9px 16px', cursor: 'pointer',
        }}>+ Open Dispute</button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>Loading…</div>
      ) : disputes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <p style={{ fontSize: 32, margin: '0 0 8px' }}>⚖️</p>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>No disputes raised. We hope it stays that way!</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 16, minHeight: 480, border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
          <div style={{ borderRight: '1px solid #e5e7eb', overflowY: 'auto' }}>
            {disputes.map(d => {
              const c = STATUS_COLOURS[d.status];
              return (
                <div key={d.id} onClick={() => setSelectedId(d.id)} style={{
                  padding: '12px 14px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                  background: selectedId === d.id ? '#eff6ff' : 'transparent',
                  borderLeft: selectedId === d.id ? '3px solid #1d4ed8' : '3px solid transparent',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>{d.title}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 5, background: c.bg, color: c.text, whiteSpace: 'nowrap' }}>{STATUS_LABELS[d.status]}</span>
                  </div>
                  <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>{CATEGORY_LABELS[d.category]} · {d.referenceNumber}</p>
                </div>
              );
            })}
          </div>
          <div>
            {selected ? (
              <DisputeDetail dispute={selected} onUpdated={handleUpdated} client={createApiClient(getToken)} />
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13 }}>Select a dispute</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
